'use client'

import React, { useMemo, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import VolunteersCardsMobile from './volunteers-cards-mobile'
import VolunteersList from './volunteers-list'
import Pagination from '@/shared/components/pagination/pagination'
import PaginationDataContextProvider from '@/shared/context/pagination-data-context'
import AddVolunteerButton from './add-volunteer-button'
import VolunteerFilterDialog from './volunteer-filter-button'
import VolunteerSearchInput from './volunteer-search-input'
import VolunteerDownloadButton from './volunteer-download-button'
import { useRegionOptions } from '@/shared/hooks/use-region-options'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { ChevronDown, X } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { isVolunteerRosterExcludedRegion } from '@/shared/constants/volunteer-region-ids'

interface FilterSearchVolunteersTabProps {
  canManageVolunteers: boolean
}

function parseRegionIdsFromParam(raw: string | null): string[] {
  if (!raw?.trim()) return []
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

function RegionalRosterSelect() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { options: allOptions, isLoading } = useRegionOptions(true)
  const options = useMemo(
    () =>
      allOptions.filter(
        (option) => !isVolunteerRosterExcludedRegion(option.value, option.label)
      ),
    [allOptions]
  )
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const urlSelectedValues = useMemo(() => {
    const allowedIds = new Set(options.map((option) => String(option.value)))
    return parseRegionIdsFromParam(searchParams.get('serveDatesRegionId')).filter((id) =>
      allowedIds.size === 0
        ? !isVolunteerRosterExcludedRegion(id)
        : allowedIds.has(id)
    )
  }, [searchParams, options])
  const [draftValues, setDraftValues] = useState<string[]>(urlSelectedValues)

  const displayValues = isOpen ? draftValues : urlSelectedValues

  const selectedOptions = useMemo(
    () =>
      displayValues
        .map((value) => options.find((option) => String(option.value) === String(value)))
        .filter(Boolean),
    [displayValues, options]
  )

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return options
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        String(option.value).toLowerCase().includes(term)
    )
  }, [options, searchTerm])

  const applySelection = (nextValues: string[]) => {
    const params = new URLSearchParams(searchParams.toString())
    if (nextValues.length === 0) {
      params.delete('serveDatesRegionId')
    } else {
      params.set('serveDatesRegionId', nextValues.join(','))
      params.set('limit', '50')
    }
    params.set('page', '1')
    router.push(`${pathname}?${params.toString()}`)
  }

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraftValues(urlSelectedValues)
      setIsOpen(true)
      return
    }
    setIsOpen(false)
    setSearchTerm('')
    const same =
      draftValues.length === urlSelectedValues.length &&
      draftValues.every((value) => urlSelectedValues.includes(value))
    if (!same) {
      applySelection(draftValues)
    }
  }

  const handleToggle = (value: string) => {
    setDraftValues((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }

  const handleRemove = (value: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (isOpen) {
      setDraftValues((prev) => prev.filter((v) => v !== value))
      return
    }
    applySelection(urlSelectedValues.filter((v) => v !== value))
  }

  return (
    <div className="w-full sm:w-[260px]">
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            role="combobox"
            aria-expanded={isOpen}
            disabled={isLoading}
            showLoader={false}
            className={cn(
              'h-10 w-full justify-between bg-white px-3 font-normal',
              selectedOptions.length > 0 && selectedOptions.length <= 2 && 'h-auto min-h-10 whitespace-normal py-1.5',
              selectedOptions.length === 0 && 'text-muted-foreground'
            )}
          >
            {selectedOptions.length > 0 ? (
              selectedOptions.length > 2 ? (
                <span className="flex-1 truncate text-left">
                  {selectedOptions.length} regions
                </span>
              ) : (
                <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                  {selectedOptions.map((option) => (
                    <span
                      key={option!.value}
                      className="inline-flex shrink-0 items-center gap-1 rounded bg-primary/10 px-2 py-0.5 text-xs text-primary"
                    >
                      {option!.label}
                      <X
                        className="h-3 w-3 shrink-0 cursor-pointer hover:text-primary/70"
                        onClick={(e) => handleRemove(String(option!.value), e)}
                      />
                    </span>
                  ))}
                </div>
              )
            ) : (
              <span>{isLoading ? 'Loading regions...' : 'Region'}</span>
            )}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <div className="p-2">
            <input
              placeholder="Search regions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-md border border-input px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="mt-2 max-h-60 space-y-1 overflow-y-auto overscroll-contain">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const checked = draftValues.includes(String(option.value))
                  return (
                    <label
                      key={option.value}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => handleToggle(String(option.value))}
                      />
                      <span className="truncate">{option.label}</span>
                    </label>
                  )
                })
              ) : (
                <p className="px-2 py-3 text-sm text-muted-foreground">No regions found</p>
              )}
            </div>
            {draftValues.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2 w-full text-muted-foreground"
                onClick={() => setDraftValues([])}
              >
                Clear regions
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default function FilterSearchVolunteersTab({ canManageVolunteers }: FilterSearchVolunteersTabProps) {
  return (
    <PaginationDataContextProvider>
      <div className="border rounded px-3 py-5 space-y-4 mb-10 hidden lg:block">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-center">
            <div className="flex gap-4 items-center justify-center min-w-0">
              <VolunteerFilterDialog />
              {canManageVolunteers && <RegionalRosterSelect />}
              <div className="flex-1 w-full">
                <VolunteerSearchInput />
              </div>
            </div>
            {canManageVolunteers && (
              <div className="flex items-center justify-end gap-3">
                <AddVolunteerButton />
                <VolunteerDownloadButton />
              </div>
            )}
          </div>
        </div>
        <VolunteersList canManageVolunteers={canManageVolunteers} />
        <Pagination />
      </div>
      <VolunteersCardsMobile />
    </PaginationDataContextProvider>
  )
}
